import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import SafeIcon from '../../common/SafeIcon';
import SEO from '../../common/SEO';
import GoogleMaps from '../../components/GoogleMaps';
import ServicesNavigationFooter from '../../components/ServicesNavigationFooter';
import RelatedTopics from '../../components/RelatedTopics';
import * as FiIcons from 'react-icons/fi';

const { 
  FiArrowLeft, FiCheckCircle, FiShield, FiVolume2, 
  FiLayers, FiActivity, FiMicOff, FiCheck, FiX, 
  FiInfo, FiMaximize, FiMinimize, FiFileText 
} = FiIcons;

function SoundInsulationDrywall() {
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Schallschutz im Trockenbau: Wie Sie Lärm effektiv aus Ihren Räumen fernhalten",
    "alternativeHeadline": "Masse-Feder-Masse Prinzip einfach erklärt",
    "image": "https://canv.wasacon.com/mk-ausbau/_assets/media/64e72a480d6e754befa23c36a971d8cf.png",
    "author": {
      "@type": "Person",
      "name": "Maxim Kelm"
    },
    "publisher": {
      "@type": "Organization",
      "name": "MK-AUSBAU",
      "logo": {
        "@type": "ImageObject",
        "url": "https://ausbau.nrw/logo-icon.svg"
      }
    },
    "datePublished": "2026-03-30",
    "description": "Ruhe im Haus durch Trockenbau? 🤫 Erfahren Sie, wie das Masse-Feder-Masse-Prinzip funktioniert und wie Sie Wände und Decken effektiv entkoppeln."
  };

  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  return (
    <div className="py-20 bg-white">
      <SEO 
        title="Schallschutz im Trockenbau: Lärm effektiv stoppen | Guide"
        description="Endlich Ruhe! 🔇 So funktioniert Schallschutz im Trockenbau: Masse-Feder-Masse, Entkopplung & die richtigen Profile. Experten-Tipps für Wände & Decken."
        keywords="Schallschutz Trockenbau, Masse-Feder-Masse Prinzip, Schallschutzwand Trockenbau, Knauf Diamant, Silentboard, Vorsatzschale Schallschutz, Luftschall Körperschall"
        schema={articleSchema}
        canonical="/blog/schallschutz-trockenbau"
      />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-left">
        <Link to="/blog" className="inline-flex items-center text-mk-teal-600 hover:text-mk-teal-700 font-bold mb-8 transition-colors group">
          <SafeIcon icon={FiArrowLeft} className="mr-2 w-5 h-5 group-hover:-translate-x-1 transition-transform" />
          Zurück zur Übersicht
        </Link>

        <motion.article {...fadeIn} className="prose prose-lg prose-mk max-w-none">
          {/* Header */}
          <header className="mb-12">
            <div className="flex items-center space-x-3 text-mk-teal-600 font-bold uppercase tracking-wider text-sm mb-4 text-left">
              <SafeIcon icon={FiVolume2} />
              <span>Bauphysik & Akustik</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight mb-6 text-left">
              Schallschutz im Trockenbau: Wie Sie Lärm effektiv aus Ihren Räumen fernhalten
            </h1>
            <div className="flex items-center space-x-4 text-gray-500 text-sm border-y border-gray-100 py-4">
              <span className="flex items-center"><SafeIcon icon={FiShield} className="mr-1" /> Experten-Ratgeber</span>
              <span className="flex items-center"><SafeIcon icon={FiCheckCircle} className="mr-1" /> Stand: März 2026</span>
            </div>
          </header>

          {/* Hero Image */}
          <div className="mb-12 rounded-2xl overflow-hidden shadow-xl aspect-video relative">
            <img 
              src="https://canv.wasacon.com/mk-ausbau/_assets/media/64e72a480d6e754befa23c36a971d8cf.png" 
              alt="Trockenbauprofile und Dämmung für Schallschutzwand"
              className="absolute top-0 left-0 w-full h-full object-cover"
            />
          </div>

          <div className="lead text-xl text-gray-600 leading-relaxed mb-12 text-left">
            Stellen Sie sich vor, Sie möchten sich entspannen – aber nebenan läuft der Staubsauger, jemand springt Seil, der Fernseher ist laut, und aus der Wohnung darunter dröhnt Musik. Würden Sie da gerne wohnen?
          </div>

          <p className="text-gray-700 mb-8">
            Lärm in Gebäuden ist selten nur „ein bisschen nervig“ – er beeinflusst Wohlbefinden, Konzentration und sogar die Gesundheit. Gleichzeitig ist es weder praktikabel noch wirtschaftlich, einfach „dickere Wände“ zu bauen, um Ruhe zu bekommen. Im modernen Innenausbau, insbesondere im Trockenbau, gibt es deutlich intelligentere Lösungen.
          </p>

          <div className="bg-mk-teal-50 p-6 rounded-xl border border-mk-teal-100 mb-12">
            <h3 className="text-mk-teal-900 font-bold mt-0 mb-4 flex items-center">
              <SafeIcon icon={FiInfo} className="mr-2"/> Was Sie in diesem Artikel lernen
            </h3>
            <ul className="mt-2 mb-0 space-y-2 text-sm list-none pl-0 text-gray-700">
              <li className="flex items-start"><SafeIcon icon={FiCheck} className="mr-2 mt-1 text-mk-teal-600"/> Wie sich Schall ausbreitet (Luftschall vs. Körperschall).</li>
              <li className="flex items-start"><SafeIcon icon={FiCheck} className="mr-2 mt-1 text-mk-teal-600"/> Warum das <strong>Masse–Feder–Masse-Prinzip</strong> der Schlüssel zu effektivem Schallschutz ist.</li>
              <li className="flex items-start"><SafeIcon icon={FiCheck} className="mr-2 mt-1 text-mk-teal-600"/> Wie Trockenbauwände und Unterdecken den Lärm technisch clever schlucken.</li>
            </ul>
          </div>

          {/* Abschnitt 1: Schallausbreitung */}
          <h2 className="text-3xl font-bold text-gray-900 mt-16 mb-6 flex items-center">
            1. Wie sich Schall ausbreitet – Grundlagen verstehen
          </h2>
          <p className="text-gray-700 mb-6">
            Schallschutz beginnt beim Verständnis, wie Schall überhaupt entsteht und sich ausbreitet. Erst wenn klar ist, wie der Lärm seinen Weg findet, können wir ihn wirksam stoppen.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            <div className="bg-white border-t-4 border-mk-teal-500 p-6 shadow-sm rounded-xl">
              <h4 className="font-bold text-gray-900 mb-2 flex items-center"><SafeIcon icon={FiMicOff} className="mr-2"/> Luftschall</h4>
              <p className="text-sm text-gray-600 mb-2">
                <strong>Was ist das?</strong> Schallwellen in der Luft (Sprache, Musik).
              </p>
              <p className="text-sm text-gray-600">
                <strong>Der Weg:</strong> Die Wellen treffen auf eine Wand, bringen sie zum Schwingen und werden auf der anderen Seite wieder abgestrahlt.
              </p>
            </div>
            <div className="bg-white border-t-4 border-mk-red-500 p-6 shadow-sm rounded-xl">
              <h4 className="font-bold text-gray-900 mb-2 flex items-center"><SafeIcon icon={FiActivity} className="mr-2"/> Körperschall</h4>
              <p className="text-sm text-gray-600 mb-2">
                <strong>Was ist das?</strong> Schwingungen in festen Bauteilen (Gehgeräusche, Bohren).
              </p>
              <p className="text-sm text-gray-600">
                <strong>Der Weg:</strong> Schall breitet sich als Querwelle über Böden und Wände aus und kann so auch entfernte Räume "mitbeschallen".
              </p>
            </div>
          </div>

          {/* Abschnitt 2: Masse-Feder-Masse */}
          <h2 className="text-3xl font-bold text-gray-900 mt-16 mb-6 flex items-center">
            <SafeIcon icon={FiLayers} className="mr-3 text-mk-teal-600" />
            2. Masse–Feder–Masse – Das Prinzip für Ruhe
          </h2>
          <p className="text-gray-700 mb-6">
            Viele glauben: "Viel hilft viel." Doch massive Wände haben Grenzen. Um den Schallschutz einer Massivwand spürbar zu verbessern, müsste man ihre <strong>Masse verdoppeln</strong>. Das ist statisch und räumlich oft unmöglich. Hier schlägt die Stunde des Trockenbaus.
          </p>

          <div className="bg-gray-900 text-white p-8 rounded-2xl mb-12 shadow-xl relative overflow-hidden">
            <div className="absolute top-0 right-0 p-4 opacity-10">
              <SafeIcon icon={FiVolume2} className="w-32 h-32" />
            </div>
            <h3 className="text-xl font-bold text-mk-teal-400 mb-4">Das geniale System im Trockenbau:</h3>
            <div className="space-y-4 relative z-10">
              <div className="flex items-center">
                <div className="w-8 h-8 rounded-full bg-mk-teal-600 flex items-center justify-center font-bold mr-4">1</div>
                <div>
                  <strong className="block text-white">Erste Masse (Beplankung A)</strong>
                  <span className="text-sm text-gray-300">Der Schall trifft auf die erste Gipskartonplatte und bringt sie leicht zum Schwingen.</span>
                </div>
              </div>
              <div className="flex items-center">
                <div className="w-8 h-8 rounded-full bg-mk-teal-600 flex items-center justify-center font-bold mr-4">2</div>
                <div>
                  <strong className="block text-white">Die Feder (Dämmung & Hohlraum)</strong>
                  <span className="text-sm text-gray-300">Die Mineralwolle im Hohlraum und das flexible Metallprofil wirken wie ein Stoßdämpfer. Sie bremsen die Schwingung ab.</span>
                </div>
              </div>
              <div className="flex items-center">
                <div className="w-8 h-8 rounded-full bg-mk-teal-600 flex items-center justify-center font-bold mr-4">3</div>
                <div>
                  <strong className="block text-white">Zweite Masse (Beplankung B)</strong>
                  <span className="text-sm text-gray-300">Die zweite Platte wird kaum noch angeregt. Der Schall ist "verhungert".</span>
                </div>
              </div>
            </div>
          </div>

          <p className="text-gray-700 italic border-l-4 border-mk-teal-500 pl-4">
            <strong>Das Ergebnis:</strong> Eine leichte Trockenbauwand kann oft mehr Schall schlucken als eine schwere Betonwand, weil die Schalen akustisch entkoppelt sind.
          </p>

          {/* Abschnitt 3: Praxis */}
          <h2 className="text-3xl font-bold text-gray-900 mt-16 mb-6">
            3. Trockenbau in der Praxis – Wände, Decken und Flanken
          </h2>
          <p className="text-gray-700 mb-6">
            Wie setzen wir dieses Wissen auf der Baustelle um? Mit Systemen, die Schallwege konsequent unterbrechen.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Die freistehende Vorsatzschale</h3>
              <p className="text-sm text-gray-600">
                Die effektivste Waffe gegen laute Nachbarn. Wir bauen eine neue Wand <strong>vor</strong> die bestehende Wand, ohne diese zu berühren.
              </p>
              <ul className="mt-3 space-y-2 text-sm text-gray-700">
                <li className="flex items-center"><SafeIcon icon={FiCheck} className="text-mk-teal-500 mr-2"/> Keine direkte Verbindung zum Mauerwerk</li>
                <li className="flex items-center"><SafeIcon icon={FiCheck} className="text-mk-teal-500 mr-2"/> Hohlraumdämmung schluckt Resonanzen</li>
                <li className="flex items-center"><SafeIcon icon={FiCheck} className="text-mk-teal-500 mr-2"/> Einsatz schwerer Platten (z.B. Knauf Silentboard)</li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Die abgehängte Akustikdecke</h3>
              <p className="text-sm text-gray-600">
                Eine einschalige Decke leitet Trittschall ungebremst weiter. Eine abgehängte Unterdecke wirkt als zweite Schale.
              </p>
              <ul className="mt-3 space-y-2 text-sm text-gray-700">
                <li className="flex items-center"><SafeIcon icon={FiCheck} className="text-mk-teal-500 mr-2"/> Akustisch entkoppelte Abhänger (Schwingbügel)</li>
                <li className="flex items-center"><SafeIcon icon={FiCheck} className="text-mk-teal-500 mr-2"/> Reduziert den "Trommeleffekt" alter Decken</li>
                <li className="flex items-center"><SafeIcon icon={FiCheck} className="text-mk-teal-500 mr-2"/> Integrierte Beleuchtung möglich</li>
              </ul>
            </div>
          </div>

          <div className="bg-orange-50 border border-orange-200 p-6 rounded-xl mb-12">
            <h4 className="font-bold text-orange-900 mb-2 flex items-center">
              <SafeIcon icon={FiMinimize} className="mr-2"/> Achtung: Flankenübertragung
            </h4>
            <p className="text-sm text-orange-800 m-0">
              Schall ist wie Wasser – er sucht sich jeden Weg. Wenn Sie nur die Trennwand dämmen, läuft der Schall oft über die durchgehende Decke oder den Estrich einfach "drumherum" (Flankenübertragung). Professioneller Schallschutz unterbricht diese Wege durch Fugen oder Schnitte im Estrich.
            </p>
          </div>

          {/* Cheat Sheet Section */}
          <div className="mt-16 mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
              <SafeIcon icon={FiFileText} className="mr-3 text-mk-teal-600" />
              Zusammenfassung als Cheat Sheet
            </h2>
            <p className="text-gray-700 mb-6">
              Speichern Sie sich diese Übersicht für Ihr Projekt ab. Ein Klick vergrößert die Ansicht.
            </p>
            
            <div 
              className="relative group cursor-zoom-in rounded-2xl overflow-hidden shadow-lg border border-gray-100 transition-all hover:shadow-2xl max-w-2xl mx-auto"
              onClick={() => setIsLightboxOpen(true)}
            >
              <img 
                src="https://canv.wasacon.com/mk-ausbau/_assets/media/cccb74913bbd25f6597ce4327c007b00.png" 
                alt="Schallschutz Trockenbau Cheat Sheet - Zusammenfassung"
                className="w-full h-auto object-cover transform transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                <div className="opacity-0 group-hover:opacity-100 bg-white/90 text-gray-900 px-6 py-3 rounded-full font-bold shadow-lg transform translate-y-4 group-hover:translate-y-0 transition-all flex items-center backdrop-blur-sm">
                  <SafeIcon icon={FiMaximize} className="mr-2" />
                  Vollbild anzeigen
                </div>
              </div>
            </div>
          </div>

          {/* Fazit */}
          <div className="bg-mk-teal-600 text-white p-10 rounded-[2.5rem] shadow-2xl mt-16 mb-12">
            <h2 className="text-white text-3xl font-bold mb-6 mt-0">Fazit: Ruhe ist planbar</h2>
            <p className="text-mk-teal-50 mb-6">
              Wer die Grundlagen der Schallausbreitung und das Masse–Feder–Masse-Prinzip verstanden hat, „hat den Schallschutz im Griff“. Reines "Draufkleben" von Material hilft selten. Es braucht intelligente, entkoppelte Konstruktionen.
            </p>
            <div className="border-t border-mk-teal-400 pt-6">
              <h4 className="font-bold text-white mb-4">Ihr nächster Schritt:</h4>
              <p className="text-white/90 text-sm mb-0">
                Gehen Sie durch Ihre Räume: Wo entsteht Lärm? Welche Bauteile vibrieren mit? Auf dieser Basis können wir gemeinsam gezielt Maßnahmen auswählen, um unerwünschte Geräusche spürbar zu reduzieren.
              </p>
            </div>
          </div>

          <div className="text-center py-12 border-t border-gray-100">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Genervt vom Lärm?</h3>
            <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
              Wir analysieren Ihre Situation vor Ort in Heinsberg und Umgebung und planen die passende Schallschutzlösung – von der Vorsatzschale bis zur Akustikdecke.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/kontakt" 
                className="bg-mk-teal-500 hover:bg-mk-teal-600 text-white px-8 py-4 rounded-sm font-bold transition-all shadow-lg flex items-center justify-center"
              >
                Beratungstermin vereinbaren
              </Link>
              <Link 
                to="/trockenbau" 
                className="bg-mk-red-700 hover:bg-mk-red-800 text-white px-8 py-4 rounded-sm font-bold transition-all shadow-lg flex items-center justify-center"
              >
                Mehr zu Trockenbau
              </Link>
            </div>
          </div>
        </motion.article>
      </div>
      
      <RelatedTopics />
      <ServicesNavigationFooter />
      <GoogleMaps />

      {/* Lightbox Modal */}
      <AnimatePresence>
        {isLightboxOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/95 p-4 backdrop-blur-md"
            onClick={() => setIsLightboxOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="relative max-w-[95vw] max-h-[95vh] flex items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              <button 
                onClick={() => setIsLightboxOpen(false)}
                className="absolute -top-12 right-0 text-white/80 hover:text-white transition-colors p-2 flex items-center gap-2 group"
              >
                <span className="text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">Schließen</span>
                <div className="bg-white/10 p-2 rounded-full group-hover:bg-white/20 transition-colors">
                  <SafeIcon icon={FiX} className="w-6 h-6" />
                </div>
              </button>
              <img 
                src="https://canv.wasacon.com/mk-ausbau/_assets/media/cccb74913bbd25f6597ce4327c007b00.png" 
                alt="Schallschutz Trockenbau Cheat Sheet - Vollbild"
                className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default SoundInsulationDrywall;